/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {Component, Vue} from 'vue-property-decorator'
import UserServiceMixin from '@/services/UserServiceMixin'
import {UserService} from '@/services/UserService'
import MessageBox from '@/components/shared/MessageBox'
import LoginServiceMixin from '@/services/LoginServiceMixin'
import {LoginService} from '@/services/LoginService'
import {Getter, Mutation} from 'vuex-class'
import {UserVO} from '@/valueObjects/UserVO'
import PasswordInput from '@/components/shared/PasswordInput'
import {ERROR_MESSAGE_TYPE} from '@/utils/constants'

@Component({
    mixins: [UserServiceMixin, LoginServiceMixin],
    components: {
        MessageBox,
        PasswordInput,
    },
})

export default class ChangePassword extends Vue {

    private isReset: boolean = false

    private title: string = 'Change your password'
    private errorMessage = ''
    private messageType = ERROR_MESSAGE_TYPE

    private oldPassword: string = ''
    private newPassword: string = ''
    private confirmPassword: string = ''

    private userService: UserService // redeclared variable to avoid warnings in Webstorm
    private loginService: LoginService // redeclared variable to avoid warnings in Webstorm

    @Getter('getCurrentUser') private currentUser: UserVO
    @Mutation('setStoreMessage') private setStoreMessage: any

    public mounted(): void {

        if (this.$route.params && this.$route.params.token) {
            this.isReset = true
            this.title = 'Reset your password'
        }
    }

    private reloadLoginPage(message: string) {
        this.sendMessageToLoginPage(message)
        this.$router.push({name: 'login'})
    }

    private sendMessageToLoginPage(message: string) {
        /* ChangePassword component is used as:
           * a child of Start view (password reset case): login redirection leads to the parent Start view. In this
                                                          case, Start view is waiting for an event with a message
           * a child of Home view (change password case): login redirection leads to non-parent Start view. Message
                                                          cannot be sent using an $emit so we use the store
        */
        if (this.isReset) {
            this.$emit('setLoginPageMessage', message)
        } else {
            this.setStoreMessage(message)
        }
    }

    private clearErrorMessage() {
        this.errorMessage = ''
    }

    private async resetPassword() {
        if (!this.validateNewPassword()) {
            return
        }

        try {
            await this.userService.passwordReset(this.newPassword, this.$route.params.token)
            this.reloadLoginPage('Password successfully changed')
        } catch (e) {
            this.errorMessage = 'Your password has not been reset. ' + e.errorMessage
        }
    }

    private async changePassword() {
        if (!this.validateNewPassword()) {
            return
        }
        try {
            await this.userService.passwordChange(this.currentUser.id, this.oldPassword, this.newPassword)
            this.reloadLoginPage('Password successfully changed, please log in with your new password')
        } catch (e) {
            if (e.errorStatusCode === 403) {
                this.errorMessage = 'Wrong old password'
            } else {
                this.errorMessage = 'Could not change your password. ' + e.errorMessage
            }
        }
    }

    private validateNewPassword(): boolean {
        if (this.newPassword === '' || this.newPassword !== this.confirmPassword) {
            this.errorMessage = 'New and confirmed passwords do not match'
            this.newPassword = ''
            this.confirmPassword = ''
            return false
        }
        return true
    }

}
